import 'react-cmdk/dist/cmdk.css'

import { useState } from 'react'
import CommandPalette, { filterItems, getItemIndex, JsonStructureItem } from 'react-cmdk'
import { useGetInstructorsQuery, useSearchClassroomQuery, useSearchStandaloneCoursesQuery } from '@Features/LandingPage/LandingPageApi/LandingPageApi'
import { logoutRequestAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import { ProgressAPI } from '@Features/Progress/ProgressSlice/ProgressSlice'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useNavigate } from 'react-router'
import Spacer from '@Components/Utilities/Spacer'
import { Typography } from '@eduact/ed-system'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@eduact/student-theme'
import Fuse from 'fuse.js'
import { trackEvent } from '@Utilities/marketingTags'

type Props = {
	open: boolean
	onClose: (_: boolean) => void
}
export const Commands = (props: Props) => {
	const [search, setSearch] = useState('')
	const [page, setPage] = useState('root')
	const { classrooms_enabled, standalone_courses_enabled } = useAppSelector(s => s.settings)
	const { data } = useSearchClassroomQuery({ searchQuery: search }, { skip: !classrooms_enabled })
	const { data: coursesSearch } = useSearchStandaloneCoursesQuery({ searchQuery: search }, { skip: !standalone_courses_enabled })
	const { data: instructors } = useGetInstructorsQuery(undefined, {
		selectFromResult: ({ data }) => {
			if (!search) return { data: data?.data || [] }
			const fuse = new Fuse(data?.data!, {
				keys: ['first_name', 'last_name'],
				shouldSort: true,
				threshold: 0.3,
				distance: 100,
			})
			return { data: fuse.search(search).map(_ => _.item) as unknown as BriefInstructor[] }
		},
	})
	const isAuth = useAppSelector(s => s.auth.isAuth)
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [t, i18n] = useTranslation('layouts', { keyPrefix: 'command_bar' })
	const { setTheme, theme } = useTheme()

	// Palette queries never reach the URL, so the /results page tracking can't
	// see them — a selection is the one meaningful moment to record the term.
	const trackPaletteSelection = (destination: string) => {
		if (search) {
			trackEvent('SearchQuery', { search_term: search, search_source: 'command_palette', search_destination: destination })
		}
	}

	const changeLanguage = () => {
		i18n.changeLanguage(i18n.language === 'ar-EG' ? 'en-GB' : 'ar-EG')
		if (i18n.language === 'ar-EG') {
			setTheme?.({ ...theme, direction: 'rtl' })
		} else {
			setTheme?.({ ...theme, direction: 'ltr' })
		}
	}

	const filteredItems = filterItems(
		[
			{
				id: 'search',
				heading: t('placeholder'),
				items: [
					...(classrooms_enabled
						? [
								{
									id: 'classrooms',
									icon: 'BookOpenIcon' as const,
									children: t('find_a_classroom'),
									onClick: () => {
										setPage('classrooms')
									},
									closeOnSelect: false,
								},
						  ]
						: []),
					...(standalone_courses_enabled
						? [
								{
									id: 'courses',
									icon: 'BookOpenIcon' as const,
									children: t('find_a_course'),
									onClick: () => {
										setPage('courses')
									},
									closeOnSelect: false,
								},
						  ]
						: []),
					{
						id: 'instructors',
						icon: 'UserCircleIcon',
						children: t('find_a_instructor'),
						onClick: () => {
							setPage('instructors')
						},
						closeOnSelect: false,
					},
				],
			},
			{
				id: 'dashboard',
				heading: 'Dashboard',
				items: [
					{
						id: 'dashboard_overview',
						icon: 'RectangleGroupIcon',
						children: t('dashboard'),
						onClick: () => {
							navigate('/dashboard')
						},
					},
					{
						id: 'profile',
						icon: 'UserCircleIcon',
						children: t('profile'),
						onClick: () => {
							navigate('/dashboard/profile')
						},
					},
					{
						id: 'settings',
						icon: 'CogIcon',
						children: t('settings'),
						onClick: () => {
							navigate('/dashboard/settings')
						},
					},
				],
			},
			{
				id: 'shortcuts',
				heading: t('shortcuts'),
				items: [
					{
						id: 'lang',
						icon: 'LanguageIcon',
						children: <Typography color="white">{t('language')}</Typography>,
						onClick: changeLanguage,
					},
				],
			},
		],
		search
	)

	const backAction: JsonStructureItem = {
		id: 'back',
		title: t('back'),

		children: (
			<Typography color="white" py="0.2rem">
				{t('back')}
			</Typography>
		),
		icon: i18n.language === 'ar-EG' ? 'ChevronRightIcon' : 'ChevronLeftIcon',
		showType: false,
		closeOnSelect: false,
		onClick: () => {
			setPage('root')
		},
	}

	return (
		<>
			<CommandPalette
				isOpen={props.open}
				onChangeOpen={() => {
					props.onClose(false)
					setPage('root')
				}}
				onChangeSearch={setSearch}
				search={search}
				page={page}
				placeholder={`${t('placeholder')}...`}
				footer={
					<div>
						{isAuth && (
							<CommandPalette.ListItem
								index={0}
								onClick={async () => {
									// Same server-side logout as the header menu — this path
									// previously cleared local state only, and did not even
									// reset the RTK cache.
									await dispatch(logoutRequestAction())
									dispatch(BaseApi.util.resetApiState())
									dispatch(ProgressAPI.util.resetApiState())
									navigate('/')
									props.onClose(false)
								}}
								showType={false}
							>
								<Spacer py="0.2rem">
									<Typography color={'white'}>{t('logout')}</Typography>
								</Spacer>
							</CommandPalette.ListItem>
						)}
					</div>
				}
			>
				<CommandPalette.Page id="root">
					{filteredItems.map(item => (
						<CommandPalette.List key={item.id} heading={item.heading}>
							{item.items.map(_item => (
								<CommandPalette.ListItem
									{..._item}
									key={_item.id}
									index={getItemIndex(filteredItems, _item.id)}
									title={_item.title}
									showType={false}
								>
									{_item.children}
								</CommandPalette.ListItem>
							))}
						</CommandPalette.List>
					))}
				</CommandPalette.Page>
				<CommandPalette.Page id="classrooms" onEscape={() => setPage('root')}>
					<CommandPalette.List>
						<CommandPalette.ListItem index={0} {...backAction} />
						{data?.data.map((item, index) => (
							<CommandPalette.ListItem
								key={item.id}
								index={index + 1}
								showType={false}
								onClick={() => {
									trackPaletteSelection('classroom')
									navigate(`/classroom/${item.label}`)
									props.onClose(false)
								}}
							>
								{item.title}
							</CommandPalette.ListItem>
						))}
					</CommandPalette.List>
				</CommandPalette.Page>
				<CommandPalette.Page id="courses" onEscape={() => setPage('root')}>
					<CommandPalette.List>
						<CommandPalette.ListItem index={0} {...backAction} />
						{coursesSearch?.data.map((item, index) => (
							<CommandPalette.ListItem
								key={item.id}
								index={index + 1}
								showType={false}
								onClick={() => {
									trackPaletteSelection('course')
									navigate(`/courses/${item.label}`)
									props.onClose(false)
								}}
							>
								{item.title}
							</CommandPalette.ListItem>
						))}
					</CommandPalette.List>
				</CommandPalette.Page>
				<CommandPalette.Page id="instructors" onEscape={() => setPage('root')}>
					<CommandPalette.List>
						<CommandPalette.ListItem index={0} {...backAction} />
						{instructors?.map((item, index) => (
							<CommandPalette.ListItem
								key={item.instructor?.label}
								index={index + 1}
								showType={false}
								onClick={() => {
									trackPaletteSelection('instructor')
									navigate(`/instructor/${item.instructor?.label}`)
									props.onClose(false)
								}}
							>
								<Typography color="white">
									{item.first_name} {item.last_name}
								</Typography>
							</CommandPalette.ListItem>
						))}
					</CommandPalette.List>
				</CommandPalette.Page>
			</CommandPalette>
		</>
	)
}
